import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Search, Music, Loader2, Save, X, Download, RefreshCw, ClipboardPaste } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

const levelOptions = ['Beginner', 'Improver', 'Intermediate', 'Advanced'];

// Extended level mapping for CopperKnob data
const levelMapping = {
  'absolute beginner': 'Beginner',
  'high beginner': 'Beginner',
  'beginner': 'Beginner',
  'high improver': 'Improver',
  'improver': 'Improver',
  'easy intermediate': 'Intermediate',
  'intermediate': 'Intermediate',
  'advanced': 'Advanced'
};

export default function AdminDancesTab() {
  const [dances, setDances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDance, setEditingDance] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Import from charts state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [chartPreview, setChartPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Bulk paste import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    choreographer: '',
    level: 'Beginner',
    count: 32
  });

  useEffect(() => {
    fetchDances();
  }, []);

  const fetchDances = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dances`);
      if (res.ok) {
        const data = await res.json();
        setDances(data);
      }
    } catch (error) {
      console.error('Error fetching dances:', error);
      toast.error('Failed to load dances');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Dance name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingDance 
        ? `${API_URL}/api/admin/dances/${editingDance._id}`
        : `${API_URL}/api/admin/dances`;
      
      const res = await fetch(url, {
        method: editingDance ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(editingDance ? 'Dance updated' : 'Dance added');
        fetchDances();
        resetForm();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save dance');
      }
    } catch (error) {
      toast.error('Failed to save dance');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (danceId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dances/${danceId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Dance deleted');
        fetchDances();
      } else {
        toast.error('Failed to delete dance');
      }
    } catch (error) {
      toast.error('Failed to delete dance');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      choreographer: '',
      level: 'Beginner',
      count: 32
    });
    setShowAddForm(false);
    setEditingDance(null);
  };

  const startEdit = (dance) => {
    setFormData({
      name: dance.name || '',
      choreographer: dance.choreographer || '',
      level: dance.level || 'Beginner',
      count: dance.count || 32
    });
    setEditingDance(dance);
    setShowAddForm(true);
  };

  // Import from Charts functions
  const openImportDialog = async () => {
    setShowImportDialog(true);
    setLoadingPreview(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dances/chart-preview?source=linedancer`);
      if (res.ok) {
        const data = await res.json();
        setChartPreview(data);
      } else {
        toast.error('Failed to load chart preview');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load chart preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImportFromCharts = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dances/import-from-charts?source=linedancer`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Imported ${result.imported} dances! (${result.skipped} already existed)`);
        setShowImportDialog(false);
        fetchDances();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Parse bulk pasted text into dance objects
  const parseBulkText = (text) => {
    let lines = text.trim().split('\n').filter(line => line.trim());
    const parsed = [];
    
    // Skip header row if detected
    if (lines.length > 0) {
      const firstLine = lines[0].toLowerCase();
      if (firstLine.includes('rank') && (firstLine.includes('name') || firstLine.includes('choreographer') || firstLine.includes('level'))) {
        lines = lines.slice(1);
      }
    }
    
    for (const line of lines) {
      let dance = {
        name: '',
        choreographer: '',
        level: 'Improver',
        count: 32
      };
      
      // Check if tab-separated (full data)
      if (line.includes('\t')) {
        const parts = line.split('\t').map(p => p.trim());
        if (parts.length >= 2) {
          // Try to parse rank if it's a number
          const maybeRank = parseInt(parts[0]);
          let startIdx = 0;
          if (!isNaN(maybeRank) && maybeRank > 0 && maybeRank <= 200) {
            startIdx = 1;
          }
          
          dance.name = parts[startIdx] || '';
          dance.choreographer = parts[startIdx + 1] || '';
          
          // Look for level and count in remaining parts
          for (let i = startIdx + 2; i < parts.length; i++) {
            const part = parts[i];
            const partLower = part.toLowerCase().trim();
            
            // Check for level using the extended mapping (CopperKnob uses detailed levels)
            const mappedLevel = Object.entries(levelMapping).find(([key]) => partLower.includes(key));
            if (mappedLevel) {
              dance.level = mappedLevel[1];
            }
            // Check for count (just a number, or number followed by "count")
            else if (/^\d+$/.test(part) || /^\d+\s*count$/i.test(part)) {
              const count = parseInt(part);
              if (count >= 8 && count <= 128) {
                dance.count = count;
              }
            }
          }
        }
      } else {
        // Single line format
        let cleanLine = line.trim();
        
        // Remove leading rank number if present
        cleanLine = cleanLine.replace(/^[#]?\d+[\.\)\s]+/, '');
        
        // Try "Name - Choreographer" format
        if (cleanLine.includes(' - ')) {
          const [namePart, ...rest] = cleanLine.split(' - ');
          dance.name = namePart.trim();
          dance.choreographer = rest.join(' - ').trim();
        }
        // Try "Name by Choreographer" format
        else if (cleanLine.toLowerCase().includes(' by ')) {
          const byIndex = cleanLine.toLowerCase().indexOf(' by ');
          dance.name = cleanLine.substring(0, byIndex).trim();
          dance.choreographer = cleanLine.substring(byIndex + 4).trim();
        }
        // Just use as name
        else {
          dance.name = cleanLine;
        }
        
        // Extract level from choreographer if present (using extended mapping)
        const choreoLower = dance.choreographer.toLowerCase();
        for (const [key, mappedLevel] of Object.entries(levelMapping)) {
          if (choreoLower.includes(key)) {
            dance.level = mappedLevel;
            dance.choreographer = dance.choreographer.replace(new RegExp(key, 'i'), '').trim();
            break;
          }
        }
        
        // Clean up choreographer
        dance.choreographer = dance.choreographer.replace(/[,\-]+$/, '').trim();
        // Remove country codes like (AUS), (UK), etc.
        dance.choreographer = dance.choreographer.replace(/\s*\([A-Z]{2,3}\)\s*$/, '').trim();
      }
      
      if (dance.name) {
        parsed.push(dance);
      }
    }
    
    return parsed;
  };

  const handleBulkTextChange = (text) => {
    setBulkText(text);
    if (text.trim()) {
      const preview = parseBulkText(text);
      setBulkPreview(preview);
    } else {
      setBulkPreview([]);
    }
  };

  const handleBulkImport = async () => {
    if (bulkPreview.length === 0) {
      toast.error('No valid dances found in pasted text');
      return;
    }
    
    setBulkImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dances/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dances: bulkPreview, mode: 'append' })
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Imported ${result.imported} dances! (${result.skipped} already existed)`);
        setShowBulkImport(false);
        setBulkText('');
        setBulkPreview([]);
        fetchDances();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      console.error('Bulk import error:', error);
      toast.error('Import failed');
    } finally {
      setBulkImporting(false);
    }
  };

  const filteredDances = dances.filter(dance => 
    dance.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dance.choreographer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dance.level?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const levelCounts = {
    Beginner: dances.filter(d => d.level === 'Beginner').length,
    Improver: dances.filter(d => d.level === 'Improver').length,
    Intermediate: dances.filter(d => d.level === 'Intermediate').length,
    Advanced: dances.filter(d => d.level === 'Advanced').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-2xl font-bold text-slate-900">{dances.length}</div>
          <div className="text-sm text-slate-500">Total Dances</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-700">{levelCounts.Beginner}</div>
          <div className="text-sm text-green-600">Beginner</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-2xl font-bold text-blue-700">{levelCounts.Improver}</div>
          <div className="text-sm text-blue-600">Improver</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="text-2xl font-bold text-purple-700">{levelCounts.Intermediate}</div>
          <div className="text-sm text-purple-600">Intermediate</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-700">{levelCounts.Advanced}</div>
          <div className="text-sm text-red-600">Advanced</div>
        </div>
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search dances..."
            className="pl-10"
          />
        </div>
        <Button
          onClick={() => { resetForm(); setShowAddForm(true); setShowBulkImport(false); }}
          className="bg-violet-600 hover:bg-violet-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Dance
        </Button>
        <Button
          onClick={() => { setShowBulkImport(!showBulkImport); setShowAddForm(false); }}
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="bulk-paste-btn"
        >
          <ClipboardPaste className="w-4 h-4 mr-2" />
          Bulk Paste
        </Button>
        <Button
          onClick={openImportDialog}
          variant="outline"
          className="border-violet-300 text-violet-700 hover:bg-violet-50"
        >
          <Download className="w-4 h-4 mr-2" />
          Import from Charts
        </Button>
      </div>

      {/* Bulk Paste Import Panel */}
      {showBulkImport && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
              <ClipboardPaste className="w-5 h-5" />
              Bulk Paste Import
            </h3>
            <Button variant="ghost" size="sm" onClick={() => { setShowBulkImport(false); setBulkText(''); setBulkPreview([]); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="text-sm text-emerald-700 space-y-1">
            <p><strong>Paste dance data in any of these formats:</strong></p>
            <ul className="list-disc list-inside text-xs space-y-0.5 ml-2">
              <li>Tab-separated: <code className="bg-emerald-100 px-1 rounded">Name ⇥ Choreographer ⇥ Level ⇥ Count</code></li>
              <li>Simple: <code className="bg-emerald-100 px-1 rounded">Dance Name - Choreographer</code></li>
              <li>Alternative: <code className="bg-emerald-100 px-1 rounded">Dance Name by Choreographer</code></li>
              <li>Just names: <code className="bg-emerald-100 px-1 rounded">One dance name per line</code></li>
            </ul>
          </div>
          
          <textarea
            value={bulkText}
            onChange={(e) => handleBulkTextChange(e.target.value)}
            placeholder="Paste your dance list here...

Example:
Cupid Shuffle - Cupid
Watermelon Crawl - Tracy Byrd
Boot Scootin' Boogie by Brooks & Dunn"
            className="w-full h-40 p-3 rounded-lg border border-emerald-300 bg-white text-slate-800 placeholder:text-slate-400 font-mono text-sm resize-none"
          />
          
          {bulkPreview.length > 0 && (
            <div className="bg-white rounded-lg border border-emerald-200 overflow-hidden">
              <div className="px-4 py-2 bg-emerald-100 border-b border-emerald-200">
                <span className="font-medium text-emerald-800">
                  Preview: {bulkPreview.length} dances detected
                </span>
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Choreographer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Level</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bulkPreview.slice(0, 20).map((dance, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 text-slate-500">{idx + 1}</td>
                        <td className="px-3 py-1.5 font-medium text-slate-900">{dance.name}</td>
                        <td className="px-3 py-1.5 text-slate-600">{dance.choreographer || '-'}</td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${
                            dance.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                            dance.level === 'Improver' ? 'bg-blue-100 text-blue-700' :
                            dance.level === 'Intermediate' ? 'bg-purple-100 text-purple-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {dance.level}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">{dance.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {bulkPreview.length > 20 && (
                  <div className="px-4 py-2 text-center text-xs text-slate-500 bg-slate-50 border-t">
                    ...and {bulkPreview.length - 20} more dances
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowBulkImport(false); setBulkText(''); setBulkPreview([]); }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkImport}
              disabled={bulkImporting || bulkPreview.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {bulkImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Import {bulkPreview.length} Dances
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-slate-50 rounded-lg p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">
              {editingDance ? 'Edit Dance' : 'Add New Dance'}
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Dance Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Cupid Shuffle"
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Choreographer
              </label>
              <Input
                value={formData.choreographer}
                onChange={(e) => setFormData({...formData, choreographer: e.target.value})}
                placeholder="e.g., Cupid"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Level
              </label>
              <select
                value={formData.level}
                onChange={(e) => setFormData({...formData, level: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-slate-200"
              >
                <option value="Beginner">Beginner</option>
                <option value="Improver">Improver</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Count
              </label>
              <Input
                type="number"
                value={formData.count}
                onChange={(e) => setFormData({...formData, count: parseInt(e.target.value) || 32})}
                min="8"
                max="128"
              />
            </div>
            
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editingDance ? 'Update' : 'Add'} Dance
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Dances Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Dance Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Choreographer</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Level</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Count</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredDances.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                    <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    {searchQuery ? 'No dances match your search' : 'No dances added yet'}
                  </td>
                </tr>
              ) : (
                filteredDances.map((dance) => (
                  <tr key={dance._id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{dance.name}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {dance.choreographer || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        dance.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                        dance.level === 'Improver' ? 'bg-blue-100 text-blue-700' :
                        dance.level === 'Intermediate' ? 'bg-purple-100 text-purple-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {dance.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {dance.count || 32}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(dance)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Dance</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{dance.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(dance._id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {filteredDances.length > 0 && (
          <div className="px-4 py-3 border-t bg-slate-50 text-sm text-slate-500">
            Showing {filteredDances.length} of {dances.length} dances
          </div>
        )}
      </div>

      {/* Import from Charts Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-violet-600" />
              Import from Linedancer Magazine Charts
            </DialogTitle>
            <DialogDescription>
              Import dances from your weekly Linedancer Magazine chart data into the Bingo Dances database.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                <span className="ml-2 text-slate-600">Loading chart data...</span>
              </div>
            ) : chartPreview ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-violet-900">
                        {chartPreview.total} dances found
                      </p>
                      <p className="text-sm text-violet-700">
                        <span className="font-semibold text-green-600">{chartPreview.new_count} new</span> dances will be imported
                        {chartPreview.total - chartPreview.new_count > 0 && (
                          <span className="text-slate-500"> • {chartPreview.total - chartPreview.new_count} already exist</span>
                        )}
                      </p>
                      {chartPreview.data_source && (
                        <p className="text-xs text-violet-500 mt-1">Source: {chartPreview.data_source}</p>
                      )}
                    </div>
                    <Button
                      onClick={openImportDialog}
                      variant="ghost"
                      size="sm"
                      disabled={loadingPreview}
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingPreview ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>

                {/* Dance List Preview */}
                {chartPreview.dances?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-700">#</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-700">Dance Name</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-700">Choreographer</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-700">Level</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {chartPreview.dances.map((dance, idx) => (
                          <tr key={idx} className={dance.already_exists ? 'bg-slate-50 text-slate-500' : ''}>
                            <td className="px-3 py-2">{dance.rank || idx + 1}</td>
                            <td className="px-3 py-2 font-medium">{dance.name}</td>
                            <td className="px-3 py-2 text-slate-600">{dance.choreographer || '-'}</td>
                            <td className="px-3 py-2">{dance.level}</td>
                            <td className="px-3 py-2">
                              {dance.already_exists ? (
                                <Badge variant="secondary" className="text-xs">Already exists</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-xs">New</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Music className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No chart data found.</p>
                    <p className="text-sm mt-1">Make sure you've pasted data in the Charts tab first.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Unable to load chart preview.</p>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImportFromCharts}
              disabled={importing || !chartPreview?.new_count}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Import {chartPreview?.new_count || 0} New Dances
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
